import { Fragment } from "react";
import {
  ConfigurationList,
  ConfigurationPaper,
} from "~/components/configuration-items";
import { useCanUpdateOrganization } from "~/lib/iam/actions/mondoo/captain/Captain/UpdateOrganization";
import { useViewer } from "~/providers/viewer";
import { useSnackbar } from "notistack";
import { getGraphQLErrors } from "~/components/ui-library";
import { SubmitHandler, useForm } from "react-hook-form";
import { Grid } from "@mui/material";
import { useOrgSettingsOutletContext } from "~/pages/organization";
import { ScopeType } from "~/hooks/useScope";
import { CreateFormInput } from "~/components/FormFields/types";
import {
  DescriptionField,
  DisplayNameField,
  IdField,
} from "~/components/FormFields";
import CompanyField from "~/components/FormFields/CompanyField";

type Props = {};

export function OrganizationNameSettings({}: Props) {
  const { org } = useOrgSettingsOutletContext();
  const { enqueueSnackbar } = useSnackbar();
  const { updateOrgName } = useViewer();

  const updateOrgPermission = useCanUpdateOrganization();

  const {
    register,
    formState: { errors, isDirty },
    handleSubmit,
    reset,
  } = useForm<CreateFormInput>({
    mode: "onBlur",
    defaultValues: {
      name: org.name,
      company: org.company ?? undefined,
      description: org.description,
      id: org.id,
    },
  });

  const handleBlur: SubmitHandler<CreateFormInput> = async (params) => {
    const { name, description, company } = params;
    if (!isDirty) return;

    try {
      await updateOrgName({
        variables: {
          input: {
            mrn: org.mrn,
            name,
            company: company ?? undefined,
            description,
          },
        },
      });

      reset(params);

      enqueueSnackbar(`Organization successfully updated`, {
        variant: "success",
      });
    } catch (error) {
      enqueueSnackbar(
        getGraphQLErrors({
          error,
          errorMessage: "Failed to update organization.",
        }),
        {
          variant: "error",
        },
      );
    }
  };

  return (
    <Fragment>
      <ConfigurationPaper>
        <ConfigurationList title="Name and Description">
          <Grid
            component="form"
            onBlur={handleSubmit(handleBlur)}
            container
            p={2}
            gap={3}
          >
            <IdField
              errors={errors}
              register={register}
              disabled
              target="organization"
            />

            <DisplayNameField
              errors={errors}
              register={register}
              disabled={!updateOrgPermission}
              target={ScopeType.Organization}
            />

            <CompanyField errors={errors} register={register} />

            <DescriptionField
              register={register}
              disabled={!updateOrgPermission}
              target={ScopeType.Organization}
            />
          </Grid>
        </ConfigurationList>
      </ConfigurationPaper>
    </Fragment>
  );
}
